/**
 * InterviewCalendarCard — monthly mini-calendar for the right "Meal" column
 * of the dashboard. Shows the current month, with any dates that have an
 * upcoming interview marked with a gold-ring circle and a small label below.
 *
 * Clicking a marked date navigates to /interviews/<id>. If multiple
 * interviews land on the same day we show a stacked indicator and link to
 * the earliest. Arrows let the user step ±1 month for planning ahead.
 *
 * Falls back gracefully:
 *   - No interviews at all → renders a quiet empty-state hint with a
 *     "Schedule an interview" CTA pointing to /interviews.
 *   - Loading → skeleton.
 *
 * Lightweight: no third-party calendar dep — just a 7-wide grid of buttons.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarDays, Loader, Plus, ChevronDown, CalendarPlus } from "lucide-react";
import { api } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function sameYMD(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

export default function InterviewCalendarCard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [upcoming, setUpcoming] = useState([]);
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  // Collapsed by default so the dashboard stays scannable — the user
  // explicitly asked for the calendar to start hidden after Resmo
  // congratulates them. Persisted across sessions so they don't have to
  // re-collapse it every page load.
  const [expanded, setExpanded] = useState(() => {
    try { return window.localStorage.getItem("resmo.calendar-expanded") === "1"; }
    catch { return false; }
  });
  const toggleExpanded = () => {
    setExpanded((v) => {
      const next = !v;
      try {
        if (next) window.localStorage.setItem("resmo.calendar-expanded", "1");
        else      window.localStorage.removeItem("resmo.calendar-expanded");
      } catch { /* noop */ }
      return next;
    });
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/interviews");
        if (alive) setUpcoming(data.upcoming || []);
      } catch { /* silent — empty state renders */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  // Bucket interviews by YYYY-MM-DD for quick lookup as we paint the grid.
  const byDate = useMemo(() => {
    const map = new Map();
    for (const iv of upcoming) {
      if (!iv.interview_at) continue;
      const d = new Date(iv.interview_at);
      if (isNaN(d.getTime())) continue;
      const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push({ ...iv, _date: d });
    }
    // Sort each day's interviews by time for the multi-interview-day case.
    for (const arr of map.values()) {
      arr.sort((a, b) => a._date.getTime() - b._date.getTime());
    }
    return map;
  }, [upcoming]);

  // Build the grid for the current cursor month: leading empty cells from the
  // previous month so dates land on the right weekday.
  const cells = useMemo(() => {
    const first = startOfMonth(cursor);
    const start = first.getDay();
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < start; i += 1) out.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) {
      out.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    }
    // Pad to a clean multiple of 7 for visual alignment.
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [cursor]);

  const today = new Date();

  const monthLabel = `${MONTH_LABELS[cursor.getMonth()]} ${cursor.getFullYear()}`;

  if (loading) {
    return (
      <div className="rm-card-static p-4" data-testid="interview-calendar-loading">
        <div className="flex items-center gap-2 font-mono-rm text-xs uppercase tracking-widest" style={{ color: "#475569" }}>
          <Loader size={14} className="animate-spin" /> Loading interview calendar…
        </div>
      </div>
    );
  }

  return (
    <div
      className="rm-card-static p-4"
      style={{ background: "white", borderColor: "#0F1B2D", boxShadow: "4px 4px 0 0 #FFC145" }}
      data-testid="interview-calendar-card"
    >
      {/* Header */}
      <button
        type="button"
        onClick={toggleExpanded}
        className="w-full flex items-center justify-between gap-2"
        style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
        aria-expanded={expanded}
        aria-controls="interview-calendar-body"
        data-testid="interview-calendar-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <CalendarDays size={16} style={{ color: "#1E40AF" }} />
          <h3 className="font-display uppercase text-sm leading-tight" style={{ color: "#0F1B2D", letterSpacing: "-0.01em" }}>
            Interview Calendar
          </h3>
          {upcoming.length > 0 && (
            <span
              className="font-mono-rm text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full"
              style={{ background: "#FFC145", color: "#0F1B2D", fontWeight: 700 }}
              data-testid="interview-calendar-count-pill"
            >
              {upcoming.length}
            </span>
          )}
        </div>
        <ChevronDown
          size={16}
          style={{
            color: "#475569",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.18s ease",
          }}
        />
      </button>

      {/* Body — only mounted when expanded so the right column stays light
          while the user is in flow elsewhere on the dashboard. */}
      {expanded && (
        <div id="interview-calendar-body" className="mt-3" data-testid="interview-calendar-body">
          {/* Month nav */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setCursor((c) => addMonths(c, -1))}
              className="rm-btn rm-btn-ghost px-1.5 py-1"
              aria-label="Previous month"
              data-testid="interview-cal-prev"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="font-mono-rm text-[10px] uppercase tracking-widest min-w-[110px] text-center" style={{ color: "#475569" }}>
              {monthLabel}
            </span>
            <button
              type="button"
              onClick={() => setCursor((c) => addMonths(c, 1))}
              className="rm-btn rm-btn-ghost px-1.5 py-1"
              aria-label="Next month"
              data-testid="interview-cal-next"
            >
              <ChevronRight size={14} />
            </button>
          </div>

      {/* Day-of-week labels */}
      <div className="grid grid-cols-7 gap-1 mb-1.5">
        {DAY_LABELS.map((d, i) => (
          <div key={i} className="text-center font-mono-rm text-[10px] uppercase" style={{ color: "#94A3B8" }}>
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="aspect-square" />;
          const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
          const interviews = byDate.get(key) || [];
          const isToday = sameYMD(d, today);
          const hasInterview = interviews.length > 0;
          const firstIv = interviews[0];
          const dotColor = hasInterview ? "#1E40AF" : "transparent";
          return (
            <button
              key={i}
              type="button"
              onClick={() => { if (firstIv) navigate(`/interviews/${firstIv.id}`); }}
              disabled={!hasInterview}
              className="aspect-square flex flex-col items-center justify-center text-[11px] font-mono-rm rounded transition-colors"
              style={{
                background: hasInterview ? "#FFC145" : "transparent",
                border: hasInterview
                  ? "2px solid #1E40AF"
                  : isToday
                    ? "1.5px dashed #0E5A4A"
                    : "1px solid transparent",
                color: hasInterview ? "#0F1B2D" : isToday ? "#0E5A4A" : "#334155",
                fontWeight: hasInterview ? 700 : isToday ? 700 : 400,
                cursor: hasInterview ? "pointer" : "default",
                position: "relative",
              }}
              title={
                hasInterview
                  ? interviews.map((iv) =>
                      `${iv._date.toLocaleString([], { hour: "numeric", minute: "2-digit" })} — ${iv.role_title || iv.role || "Interview"}${(iv.role_company || iv.company) ? " @ " + (iv.role_company || iv.company) : ""}`,
                    ).join("\n")
                  : ""
              }
              data-testid={hasInterview ? `interview-cal-day-${d.getDate()}` : undefined}
            >
              <span>{d.getDate()}</span>
              {/* Dot indicator beneath the number for multi-interview days */}
              {interviews.length > 1 && (
                <span
                  className="block rounded-full mt-0.5"
                  style={{ width: 4, height: 4, background: dotColor }}
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Empty state CTA */}
      {upcoming.length === 0 && (
        <div className="mt-3 pt-3" style={{ borderTop: "1px dashed #CBD5E1" }}>
          <div className="text-[11px] leading-snug mb-2" style={{ color: "#475569" }}>
            Nothing on the books yet. Schedule an interview to set a countdown,
            generate a briefing, and unlock Resmo's prep tools.
          </div>
          <button
            type="button"
            onClick={() => navigate("/interviews")}
            className="rm-btn rm-btn-primary text-[11px] px-3 py-1.5"
            data-testid="interview-cal-schedule-cta"
          >
            <Plus size={11} /> Schedule one
          </button>
        </div>
      )}

      {/* Upcoming list summary */}
      {upcoming.length > 0 && (
        <div className="mt-3 pt-3 space-y-1" style={{ borderTop: "1px dashed #CBD5E1" }}>
          <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8", fontWeight: 700 }}>
            · {upcoming.length} upcoming · tap a circled date
          </div>
          {upcoming.slice(0, 3).map((iv) => {
            const d = new Date(iv.interview_at);
            const icsHref = iv.ics_url ? `${BACKEND_URL}${iv.ics_url}` : null;
            const icsFilename = `resmo-interview-${(iv.id || "").slice(0, 8)}.ics`;
            return (
              <div
                key={iv.id}
                className="flex items-center gap-1 rounded px-2 py-1 hover:bg-slate-50 transition-colors"
                data-testid={`interview-cal-row-${iv.id}`}
              >
                <button
                  type="button"
                  onClick={() => navigate(`/interviews/${iv.id}`)}
                  className="flex-1 min-w-0 text-left"
                  style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                >
                  <div className="text-[12px] truncate" style={{ color: "#0F1B2D", fontWeight: 600 }}>
                    {(iv.role_title || iv.role) || "Interview"}{(iv.role_company || iv.company) ? " · " + (iv.role_company || iv.company) : ""}
                  </div>
                  <div className="text-[10px] font-mono-rm uppercase" style={{ color: "#475569" }}>
                    {d.toLocaleString([], {
                      weekday: "short", month: "short", day: "numeric",
                      hour: "numeric", minute: "2-digit",
                    })}
                  </div>
                </button>
                {icsHref && (
                  <a
                    href={icsHref}
                    download={icsFilename}
                    onClick={(e) => e.stopPropagation()}
                    title="Add this interview to your calendar (.ics)"
                    aria-label={`Add ${iv.role_title || iv.role || "interview"} to your calendar`}
                    className="flex-shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono-rm text-[10px] uppercase tracking-widest transition-transform hover:translate-y-[-1px]"
                    style={{
                      background: "#0F1B2D",
                      color: "#FFC145",
                      border: "1.5px solid #0F1B2D",
                      fontWeight: 700,
                      textDecoration: "none",
                      boxShadow: "2px 2px 0 0 #FFC145",
                    }}
                    data-testid={`interview-cal-ics-${iv.id}`}
                  >
                    <CalendarPlus size={11} />
                    <span className="hidden sm:inline">Add</span>
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
        </div>
      )}
    </div>
  );
}
